import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonBadge,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonFooter,
  IonButton,
  IonRow,
  IonCol,
  IonAlert,
  IonSkeletonText,
  IonCheckbox
} from '@ionic/react';

import './AppointmentCard.scss';

import { Appointment } from '../models';
import capitalize from '../utils/capitalize';
import {
  APPOINTMENT_CHECKIN,
  APPOINTMENT_CHECKIN_COVID
} from '../navigators/AppStackNavigator';

interface Props {
  appointment?: Appointment;
  loading?: boolean;
  onBeforeDelete?: Function;
  onDelete?: Function;
  deletable?: boolean;
  selectable?: boolean;
  selected?: boolean;
  opaque?: boolean;
  showCheckin?: boolean;
  onSelect?: (appointment: Appointment) => void;
}

const AppointmentCard: React.FC<Props> = ({
  appointment,
  loading = false,
  onBeforeDelete = () => {},
  onDelete = () => {},
  deletable = true,
  selectable = false,
  selected = false,
  opaque = false,
  showCheckin = false,
  onSelect = () => {}
}) => {
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);

  if (!appointment || loading) {
    return (
      <IonCard>
        <IonRow>
          <IonCol size="12" sizeMd="5">
            <IonCardHeader>
              <IonRow className="ion-justify-content-between">
                <IonCol>
                  <IonCardTitle>
                    <IonSkeletonText animated style={{ width: '50%' }} />
                  </IonCardTitle>
                  <IonCardSubtitle>
                    <IonSkeletonText animated style={{ width: '30%' }} />
                  </IonCardSubtitle>
                </IonCol>
              </IonRow>
            </IonCardHeader>
          </IonCol>
          <IonCol size="12" sizeMd="6">
            <IonCardContent className="ion-no-padding-top-on-mobile">
              <IonSkeletonText animated style={{ width: '88%' }} />
              <IonSkeletonText animated style={{ width: '70%' }} />
              <IonSkeletonText animated style={{ width: '60%' }} />
            </IonCardContent>
          </IonCol>
        </IonRow>
      </IonCard>
    );
  }

  return (
    <IonCard
      className={[
        appointment.isFinished() ? 'appointment-card--finished' : '',
        opaque ? 'appointment-card--opaque' : '',
        selectable ? 'appointment-card--selectable' : '',
        selected ? 'appointment-card--selected' : ''
      ].join(' ')}
      onClick={() => onSelect(appointment)}
    >
      <IonRow>
        {selectable ? (
          <IonCol size="1">
            <IonCardContent>
              <IonCheckbox color="primary" checked={selected} />
            </IonCardContent>
          </IonCol>
        ) : null}
        <IonCol size={selectable ? '11' : '12'} sizeMd={selectable ? '5' : '6'}>
          <IonCardHeader>
            <IonRow className="ion-justify-content-between">
              <IonCol>
                <IonCardTitle>{appointment.getDayString()}</IonCardTitle>
                <IonCardSubtitle>{appointment.getHourString()}</IonCardSubtitle>
              </IonCol>
              <IonCol size="auto" sizeMd="12">
                <IonBadge
                  color={appointment.getStatusMeta()?.className}
                  className="ion-margin-bottom"
                >
                  {capitalize(`${appointment?.getStatusMeta()?.label}`)}
                </IonBadge>
              </IonCol>
            </IonRow>
          </IonCardHeader>
        </IonCol>
        <IonCol size="12" sizeMd="6">
          <IonCardContent className="ion-no-padding-top-on-mobile">
            {appointment.reference ? (
              <div className="ion-margin-bottom">
                <strong>Numéro de dossier</strong>
                <br />
                {appointment.reference}
              </div>
            ) : null}
            {appointment.laboratory?.name}
          </IonCardContent>
        </IonCol>
      </IonRow>
      {deletable ? (
        <IonFooter className="appointment-card__footer">
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <IonCol size="auto">
              <IonButton
                onClick={() => setShowConfirmationAlert(true)}
                fill="clear"
                disabled={appointment.isFinished()}
              >
                Annuler
              </IonButton>
            </IonCol>
            {showCheckin && appointment.isCheckinAllowed() ? (
              <IonCol size="auto">
                <IonButton
                  href={`${
                    appointment.laboratory?.covid
                      ? APPOINTMENT_CHECKIN_COVID
                      : APPOINTMENT_CHECKIN
                  }/${appointment.laboratory?.slug}`}
                  fill={'clear'}
                  disabled={appointment.isFinished()}
                >
                  Signaler ma présence en laboratoire
                </IonButton>
              </IonCol>
            ) : null}
          </IonRow>
        </IonFooter>
      ) : null}
      <IonAlert
        isOpen={showConfirmationAlert}
        onDidDismiss={() => setShowConfirmationAlert(false)}
        header={`Voulez-vous vraiment annuler ce créneau de prise en charge ?`}
        subHeader={'Attention, cette action est irreversible.'}
        buttons={[
          {
            text: 'Non',
            role: 'cancel',
            handler: () => setShowConfirmationAlert(false)
          },
          {
            text: 'Oui',
            handler: async () => {
              setShowConfirmationAlert(false);
              onBeforeDelete();
              await appointment.delete();
              onDelete();
            }
          }
        ]}
      />
    </IonCard>
  );
};

export default AppointmentCard;
